import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Button } from 'shared/ui/Button';
import { Loader } from 'shared/ui/Loader';

type EditorWrapperProps = {
	filePreview: ReactNode;
	sidebar: ReactNode;
	onSave: () => void;
	onRun?: () => void;
	isSaving?: boolean;
	isLoading?: boolean;
};

const EditorWrapper: FC<EditorWrapperProps> = ({
	filePreview,
	sidebar,
	onSave,
	onRun,
	isSaving = false,
	isLoading = false,
}) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [rightPanelWidth, setRightPanelWidth] = useState(() => {
		const saved = localStorage.getItem('filePreview.rightPanelWidth');
		return saved ? Number(saved) : 30;
	});
	const containerRef = useRef<HTMLDivElement>(null);
	const isDragging = useRef(false);

	const handleDragStart = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		isDragging.current = true;

		const onMouseMove = (moveEvent: MouseEvent) => {
			if (!isDragging.current || !containerRef.current) return;
			const containerRect = containerRef.current.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const offsetFromRight = containerRect.right - moveEvent.clientX;
			const newWidthPercent = (offsetFromRight / containerWidth) * 100;
			const clamped = Math.min(60, Math.max(30, newWidthPercent));
			setRightPanelWidth(clamped);
			localStorage.setItem('filePreview.rightPanelWidth', String(clamped));
		};

		const onMouseUp = () => {
			isDragging.current = false;
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
	}, []);

	if (isLoading) return <Loader />;

	return (
		<>
			<Box
				ref={containerRef}
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					width: '100%',
					gap: 1,
					flex: 1,
					minHeight: 0,
				}}
			>
				{filePreview}
				<Box
					onMouseDown={handleDragStart}
					sx={{
						width: '6px',
						cursor: 'col-resize',
						backgroundColor: ColorPalette.periwinkle,
						transition: 'background-color 0.15s',
						borderRadius: '3px',
						alignSelf: 'stretch',
						position: 'sticky',
						top: 0,
					}}
				/>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						width: `${rightPanelWidth}%`,
						minWidth: '30%',
						flexShrink: 0,
						alignSelf: 'stretch',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '20px',
							flex: 1,
							minHeight: 0,
							overflowY: 'auto',
							overflowX: 'hidden',
						}}
					>
						{sidebar}
					</Box>

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							flexShrink: 0,
							paddingTop: '16px',
							marginTop: '16px',
							borderTop: `1px solid ${ColorPalette.periwinkle}`,
						}}
					>
						<Button
							size={'small'}
							variant={'outlined'}
							disabled={isSaving}
							onClick={() => navigate(-1)}
						>
							{t('shared_texts.cancel')}
						</Button>
						{onRun && (
							<Button size="small" onClick={onRun} loading={isSaving}>
								{t('shared_texts.run')}
							</Button>
						)}
						<Button size={'small'} loading={isSaving} onClick={onSave}>
							{t('shared_texts.save')}
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default EditorWrapper;
